import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest } from "@/lib/api";
import Select from "react-select";
import moment from "moment";



export interface AuditLogItem {
  audit_id: string;
  timestamp: string;
  user_id?: string;
  user_email?: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  method: string;
  endpoint: string;
  status_code: number;
  success: boolean;
  details?: Record<string, any>;
  request_id?: string;
  error_message?: string;
  response_time_ms?: number;
  created_at: string;
  app_name?: string;
}

interface AuditLogsResponse {
  items: AuditLogItem[];
  total_count: number;
  page: number;
  limit: number;
}

interface UsersListResponse {
  items: UserItem[];
  total_count: number;
  page: number;
  limit: number;
}


interface UserItem {
  user_id: string;
  email: string;
  groups: string[];
  roles: string[];
  permissions: string[];
}

interface AIApp {
  id: string;
  appName: string;
  description: string;
  icon: string;
  iconKey?: string;
  app_url: string;
  backend_url: string;
  route: string;
  usersgroups: string[] | string;
  isActive: boolean;
}

export function AuditLogsManager() {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [apps, setApps] = useState<AIApp[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // filters
  const [selectedUser, setSelectedUser] = useState<any>("");
  const [selectedApp, setSelectedApp] = useState<any>("");
  const [users, setUsers] = useState<UserItem[]>([]);

  const loadLogs = async (
    app_name: string = "",
    user_id: string = "",
    pageToLoad: number = 1,
    overrideLimit?: number
  ) => {
    setLogsLoading(true);
    setError("");
    try {
      let path = "/admin/audit-logs";
      const params = new URLSearchParams();
      params.set("page", String(pageToLoad));
      params.set("limit", String(overrideLimit ?? limit));
      if (app_name) {
        params.set("app_name", app_name);
      }
      if (user_id) {
        params.set("user_id", user_id);
      }

      const fullPath = `${path}?${params.toString()}`;
      const data = await apiRequest<AuditLogsResponse>(fullPath);
      setLogs(data.items ?? []);
      setTotalCount(data.total_count ?? 0);
      setCurrentPage(data.page ?? pageToLoad);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit logs");
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadApps = async () => {
    setError("");
    try {
      let data: AIApp[] = await apiRequest<AIApp[]>("/admin/applications");
      data = data.map((app) => ({
        ...app,
        value: app.id,
        label: app.appName,
      }));
      setApps(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load applications");
    }
  };


  const loadUsers = async () => {
    setError("");
    try {
      const data = await apiRequest<UsersListResponse>("/admin/users?page=1&limit=500");
      const usersList = (data.items ?? []).map((user) => ({
        ...user,
        value: user.user_id,
        label: user.email,
      }));

      setUsers(usersList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    }
  };


  useEffect(() => {
    void loadLogs();
    void loadUsers();
    void loadApps();
  }, []);

  const applyFilters = () => {
    void loadLogs(selectedApp?.label || "", selectedUser?.value || "", 1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / limit)), [totalCount, limit]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }
    if (currentPage >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }, [currentPage, totalPages]);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      void loadLogs(selectedApp?.label || "", selectedUser?.value || "", currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      void loadLogs(selectedApp?.label || "", selectedUser?.value || "", currentPage + 1);
    }
  };

  const handleLimitChange = (selected: { value: number; label: string } | null) => {
    const nextLimit = selected?.value ?? 100;
    setLimit(nextLimit);
    void loadLogs(selectedApp?.label || "", selectedUser?.value || "", 1, nextLimit);
  };



  return (
    <Card>
      <CardHeader className="flex items-center justify-between space-y-0 flex-row-reverse">
        {/* <CardTitle className="text-xl">Audit Logs</CardTitle> */}
        <Button onClick={() => loadLogs(selectedApp?.label || "", selectedUser?.value || "", 1)}>
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4 justify-end">
          <Select
            className="react-select-container min-w-[350px]"
            classNamePrefix="react-select"
            placeholder="Select User"
            isClearable
            value={
              selectedUser
            }
            onChange={(selected: any) => setSelectedUser(selected)}
            options={users}
          />
          <Select
            className="react-select-container min-w-[250px]"
            classNamePrefix="react-select"
            placeholder="Select Application"
            isClearable
            value={
              selectedApp
            }
            onChange={(selected: any) => setSelectedApp(selected)}
            options={apps}
          />

          {/* <Input
          placeholder="Resource Type"
          value={resourceTypeFilter}
          onChange={(e) => setResourceTypeFilter(e.target.value)}
        />
        <Input
          placeholder="Resource ID"
          value={resourceIdFilter}
          onChange={(e) => setResourceIdFilter(e.target.value)}
        />
        <Input
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Input
          type="number"
          placeholder="Limit"
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 0))}
        /> */}
          <div className="flex items-center">
            <Button onClick={applyFilters}>Apply</Button>
          </div>
        </div>

        {error ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="relative h-[520px] overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Timestamp</th>
                <th className="px-3 py-2 text-left font-medium">User</th>
                <th className="px-3 py-2 text-left font-medium">Service Name</th>
                <th className="px-3 py-2 text-left font-medium">Action</th>
                <th className="px-3 py-2 text-left font-medium">Resource</th>
                <th className="px-3 py-2 text-left font-medium">Endpoint</th>
                <th className="px-3 py-2 text-left font-medium">Response Time</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Success</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-center text-sm text-muted-foreground" colSpan={9}>
                    {logsLoading ? "" : "No audit logs found."}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.audit_id} className="border-t">
                    <td className="px-3 py-2">{moment(log.timestamp).format("YYYY-MM-DD HH:mm:ss")}</td>
                    <td className="px-3 py-2">{log.user_email || log.user_id || "-"}</td>
                    <td className="px-3 py-2">{log.app_name || "-"}</td>
                    <td className="px-3 py-2">{log.action}</td>
                    <td className="px-3 py-2">{log.resource_type}</td>
                    <td className="px-3 py-2">{log.endpoint}</td>
                    <td className="px-3 py-2">{log.response_time_ms ? `${log.response_time_ms} ms` : "-"}</td>
                    <td className="px-3 py-2">{log.status_code}</td>
                    <td className="px-3 py-2">{log.success ? "Yes" : "No"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {logsLoading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Limit</span>
            <Select
              className="react-select-container min-w-[140px]"
              classNamePrefix="react-select"
              value={{ value: limit, label: `${limit}` }}
              onChange={(selected) =>
                handleLimitChange(selected as { value: number; label: string } | null)
              }
              options={[
                { value: 25, label: "25" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
                { value: 200, label: "200" },
                { value: 500, label: "500" },
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({totalCount} total logs)
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={logsLoading || currentPage === 1}
            >
              Previous
            </Button>
            {visiblePageNumbers.map((pageNumber) => (
              <Button
                key={pageNumber}
                variant={pageNumber === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  void loadLogs(selectedApp?.label || "", selectedUser?.value || "", pageNumber)
                }
                disabled={logsLoading}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={logsLoading || currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
